import { PartnerName } from '@melio/platform-api';

import { CapOneIcons } from '@/cl/icons/capone';
import { CloverIcons } from '@/cl/icons/clover';
import { MelioIcons } from '@/cl/icons/melio';
import { UsBankIcons } from '@/cl/icons/usbank';
import { capOneTheme, cloverTheme, fiservTheme, melioTheme } from '@/cl/theme/partners';
import cloverTranslation from '@/translations/partners/clover.locale.json';
import fiservTranslation from '@/translations/partners/fiserv.locale.json';
import melioTranslation from '@/translations/partners/melio.locale.json';
import { sbbConfig } from './configByPartner/sbb.config';
import capOneTranslation from './translations/partners/capOne.locale.json';
import sbbTranslation from './translations/partners/sbb.locale.json';
import {
  generateMelioRedirectPath,
  generatePaymentPagePath,
  getMeliocomOrgUrl,
} from './utils/generateMelioRedirectPath';
import { capitalOneConfig, cloverConfig, fiservConfig, fiservUsBankConfig, melioConfig } from './configByPartner';
import { Features, PartnerConfig, PartnersConfig } from './partnersConfig.types';

export const PartnersConfiguration: PartnersConfig = {
  [PartnerName.Melio]: {
    displayName: 'Melio',
    partnerNameAnalyticsEvents: 'melio',
    partnerProductName: 'Melio',
    tabTitle: 'Melio',
    theme: melioTheme,
    config: melioConfig,
    icons: MelioIcons,
    translations: melioTranslation,
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'token',
        orgId: 'orgId',
        userId: 'userId',
        terms: 'terms',
        mock: 'mock',
        target: 'target',
        targetId: 'targetId',
        entryPoint: 'entryPoint',
        redirectUrl: 'redirect_url',
      },
      redirectWhenMissingToken: true,
    },
    supportEmail: 'support@meliopayments.com',
    showOnboardingSwiper: true,
    allowEditCompanyDetails: true,
    allowEditFirstAndLastName: true,
    hideFooter: true,
    skipLogoutSuccessful: true,
    generatePaymentPagePath,
    features: {
      [Features.GetPaid]: {
        redirectUrls: {
          getPaid: (accessToken) => generateMelioRedirectPath(accessToken, 'get-paid'),
          customers: (accessToken) => generateMelioRedirectPath(accessToken, 'contacts/customers'),
        },
      },
      [Features.AccountingFirm]: {
        redirectUrls: {
          clients: (accessToken) => generateMelioRedirectPath(accessToken, 'companies'),
          team: (accessToken) => generateMelioRedirectPath(accessToken, 'team'),
        },
      },
      [Features.Expenses]: {
        redirectUrls: {
          expenses: (accessToken) => generateMelioRedirectPath(accessToken, 'spend-management'),
        },
      },
      [Features.ReceivingMethods]: {
        learnMoreUrl: {
          zendeskLink:
            'https://melio.zendesk.com/hc/en-us/articles/4416227178908-How-can-I-pay-the-credit-card-fee-on-behalf-of-my-customers',
          melioPaymentsLink:
            'https://help.meliopayments.com/en/articles/5187223-how-can-i-pay-the-credit-card-fee-on-behalf-of-my-customer',
        },
      },
    },
    getCurrentOrgUrl: getMeliocomOrgUrl,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: true,
      approvalByOtherActions: true,
    },
  },
  [PartnerName.CapitalOne]: {
    displayName: 'Capital One Business',
    partnerNameAnalyticsEvents: 'capital-one',
    partnerProductName: 'Pay Vendors',
    tabTitle: 'Pay Vendors',
    theme: capOneTheme,
    config: capitalOneConfig,
    icons: CapOneIcons,
    translations: capOneTranslation,
    supportEmail: 'capitalonevendorpayments@melio.com',
    supportQuestionIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'assertion',
        orgId: 'id',
        terms: 'acceptedTerms',
        target: 'target',
        targetId: 'targetId',
        entryPoint: 'entryPoint',
        userRole: 'userRole',
        backButtonReferenceId: 'id',
      },
    },
    showOnboardingSwiper: true,
    showOnboardingBackToPartner: true,
    billsInbox: {
      learnMoreLink: 'https://capitalonevendorpayments.zendesk.com/hc/en-us/articles/6482836621852',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: false,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
      billQbSynced: true,
    },
  },
  [PartnerName.Clover]: {
    displayName: 'Clover',
    partnerNameAnalyticsEvents: 'clover',
    partnerProductName: 'Pay Bills',
    tabTitle: 'Pay Bills by Melio',
    tabsVariant: 'neutral',
    theme: cloverTheme,
    config: cloverConfig,
    icons: CloverIcons,
    translations: cloverTranslation,
    supportEmail: 'support-billpay@clover.melio.com',
    supportQuestionIds: [1, 2, 4, 5, 6, 3, 7, 8, 9, 10],
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'code',
        orgId: 'merchant_id',
        userId: 'employee_id',
      },
      redirectWhenMissingToken: true,
    },
    showOnboardingSwiper: false,
    billsInbox: {
      learnMoreLink: 'https://paybillsbymelio.zendesk.com/hc/en-us/articles/5401742662812',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: false,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
    },
  },
  [PartnerName.Fiserv]: {
    displayName: 'Us Bank',
    partnerNameAnalyticsEvents: 'us-bank',
    partnerProductName: 'Pay Bills',
    tabTitle: 'Pay Bills',
    theme: fiservTheme,
    config: fiservConfig,
    icons: UsBankIcons,
    translations: fiservTranslation,
    supportEmail: 'us-bank-payments@melio.com',
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'token',
      },
    },
    showOnboardingSwiper: false,
    billsInbox: {
      learnMoreLink: 'https://paybillsbymelio.zendesk.com/hc/en-us/articles/5401742662812',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: true,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
    },
  },
  [PartnerName.FiservUsBank]: {
    displayName: 'Us Bank',
    partnerNameAnalyticsEvents: 'us-bank',
    partnerProductName: 'Pay Bills',
    tabTitle: 'Pay Bills',
    theme: fiservTheme,
    config: fiservUsBankConfig,
    icons: UsBankIcons,
    translations: fiservTranslation,
    supportEmail: 'us-bank-payments@melio.com',
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'token',
      },
    },
    showOnboardingSwiper: false,
    billsInbox: {
      learnMoreLink: 'https://paybillsbymelio.zendesk.com/hc/en-us/articles/5401742662812',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: true,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
    },
  },
  [PartnerName.DeploymentsDemo]: {
    displayName: 'Us Bank',
    partnerNameAnalyticsEvents: 'us-bank',
    partnerProductName: 'Pay Bills',
    tabTitle: 'Pay Bills',
    theme: fiservTheme,
    config: fiservUsBankConfig,
    icons: UsBankIcons,
    translations: fiservTranslation,
    supportEmail: 'us-bank-payments@melio.com',
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'token',
      },
    },
    showOnboardingSwiper: false,
    billsInbox: {
      learnMoreLink: 'https://paybillsbymelio.zendesk.com/hc/en-us/articles/5401742662812',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: true,
    supportedNotificationGroupsOptions: {
      billDueReminder: true,
      paymentByMeScheduled: true,
      paymentByMeUpdated: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
    },
  },
  [PartnerName.Shopify]: {
    // shopify is not in use in the platform-app
  } as PartnerConfig,
  [PartnerName.BuiltTechnologies]: {
    // Built is not in use in the platform-app
  } as PartnerConfig,
  [PartnerName.WellsFargo]: {
    // ????
  } as PartnerConfig,
  [PartnerName.Sbb]: {
    displayName: 'Capital One Business',
    partnerNameAnalyticsEvents: 'sbb',
    partnerProductName: 'Pay Business Bills',
    tabTitle: 'Pay Business Bills',
    theme: capOneTheme,
    config: sbbConfig,
    icons: CapOneIcons,
    translations: sbbTranslation,
    supportEmail: 'support@capitalonebusinessbills.melio.com',
    supportQuestionIds: [1, 5, 2, 13, 8, 10, 9, 3, 11, 12],
    oAuth: {
      required: true,
      queryParamsMapping: {
        token: 'assertion',
        orgId: 'bid',
        terms: 'acceptedTerms',
        target: 'target',
        targetId: 'targetId',
        entryPoint: 'entryPoint',
        backButtonReferenceId: 'id',
      },
    },
    showOnboardingSwiper: true,
    billsInbox: {
      learnMoreLink:
        'https://capitalonebusinessbills.zendesk.com/hc/en-us/articles/9702494960668-How-to-automatically-receive-bills-to-your-Bills-inbox',
    },
    allowEditCompanyDetails: false,
    allowEditFirstAndLastName: false,
    supportedNotificationGroupsOptions: {
      paymentByMeScheduled: false,
      paymentByMeUpdated: false,
      billDueReminder: true,
      feeReceipt: true,
      paymentByOtherScheduled: true,
      approvalByMeRequired: false,
      approvalByOtherActions: false,
    },
  },
};
